'use client';

import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown, ChevronRight, Settings, User } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { MenuItem, menuItems } from "./menuData";
import LogoutButton from "../auth/LogoutButton";
import "./Sidebar.css";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthData } from "@/hooks/useAuthData";
import { useUtilisateurs } from "@/hooks/useUtilisateurs";
import { UserPermissions } from "@/entity/types/userPermissions";
import fixTextEncoding from "@/utils/textEncoding";
import { useIsMobile } from "@/hooks/useIsMobile";
import { SeericButton } from "../SeericButton/SeericButton";
import { SITE_LOGO, SITE_LOGO_ALT, SITE_NAME_CAPITALIZED } from "@/constants/site";
import { useUnreadCountContext } from "@/hooks/contexts/UnreadCountContext";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

// Badge notification inline
const NotificationBadge: React.FC<{ count: number; loading?: boolean }> = ({ count, loading = false }) => {
  if (count === 0 || loading) return null;
  const displayCount = count.toString();

  return (
    <span
      className="notification-badge"
      style={{
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        background: '#ef4444',
        color: 'white',
        borderRadius: '50%',
        minWidth: '18px',
        height: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7rem',
        fontWeight: '600',
        lineHeight: '1',
        padding: '0 4px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '2px solid var(--background)',
        zIndex: 10
      }}
    >
      {displayCount}
    </span>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const pathname = usePathname();
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const { user } = useAuthData();
  const { permissions } = useUtilisateurs();
  const { count: unreadCount, loading: countLoading } = useUnreadCountContext();
  const isMobile = useIsMobile();
  const router = useRouter();

  useEffect(() => {
    const firstSubMenuId = menuItems.find(item => item.subItems)?.id;
    if (firstSubMenuId) {
      setOpenMenus([firstSubMenuId]);
    }
  }, []);

  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, open]);

  const isActive = (href: string) => pathname === href;

  const toggleMenu = (itemId: string) => {
    setOpenMenus(prev =>
      prev.includes(itemId)
        ? []
        : [itemId]
    );
  };

  function filterMenu(items: MenuItem[], permissions: UserPermissions | null): MenuItem[] {
    if (!permissions) return [];

    const hasEspaceRelances = permissions.EspaceRelances;

    return items
      .filter(item => {
        if (item.id === 'relances' && !hasEspaceRelances) {
          return false;
        }
        // Filtrer selon les permissions
        return !item.permission || permissions[item.permission];
      })
      .map(item => ({
        ...item,
        subItems: item.subItems
          ? item.subItems.filter(sub => !sub.permission || permissions[sub.permission])
          : undefined,
      }))
      .filter(item => !item.subItems || item.subItems.length > 0 || !item.permission || permissions[item.permission]);
  }

  const isMessagesItem = (item: any): boolean => {
    const label = (item?.label || '').toLowerCase();
    const href = item?.href || '';
    const id = item?.id || '';
    return label.includes('message') || href.includes('messages') || id === 'messages' || href === '/messages';
  };

  if (!open && !isMobile) return null;

  const filteredMenu = filterMenu(menuItems, permissions);

  return (
    <>
      {isMobile && open && (
        <div
          className="sidebar-backdrop"
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--sidebar-background)',
            zIndex: 999
          }}
        />
      )}

      <AnimatePresence>
        {open && (
          <motion.aside
            className="sidebar"
            initial={{ x: isMobile ? '-100%' : 0, opacity: isMobile ? 1 : 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: isMobile ? '-100%' : 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{
              width: isMobile ? '80%' : 240,
              maxWidth: 300,
              overflow: "hidden",
              position: "fixed",
              top: 0,
              left: 0,
              height: "100vh",
              background: 'var(--background)',
              zIndex: 1000
            }}
          >
            <div className="sidebar-header">
              <div className="sidebar-logo">
                <Image
                  src={SITE_LOGO}
                  alt={SITE_LOGO_ALT}
                  width={28}
                  height={28}
                />
                <div>
                  <span className="sidebar-brand">
                    {SITE_NAME_CAPITALIZED}
                  </span>
                  <div className="sidebar-subtitle">Groupe Finrec</div>
                </div>
              </div>
              <button onClick={onClose} className="sidebar-close">✕</button>
            </div>

            <nav className="sidebar-nav">
              {filteredMenu.map((item, index) => (
                <div key={index} className="sidebar-nav-item">
                  {item.subItems ? (
                    <button onClick={() => toggleMenu(item.id)} className="sidebar-nav-btn">
                      <span className="flex items-center gap-2" style={{ position: 'relative' }}>
                        {item.icon}
                        {item.label}
                        {isMessagesItem(item) && (
                          <NotificationBadge count={unreadCount} loading={countLoading} />
                        )}
                      </span>
                      {openMenus.includes(item.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      {item.openInNewTab ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`sidebar-nav-link${isActive(item.href) ? " active" : ""}`}
                        >
                          {item.icon}
                          {item.label}
                        </a>
                      ) : (
                        <Link
                          href={item.href}
                          className={`sidebar-nav-link${isActive(item.href) ? " active" : ""}`}
                        >
                          {item.icon}
                          {item.label}
                        </Link>
                      )}
                      {isMessagesItem(item) && (
                        <NotificationBadge count={unreadCount} loading={countLoading} />
                      )}
                    </div>
                  )}

                  {item.subItems && openMenus.includes(item.id) && (
                    <div className="sidebar-subitems">
                      {item.subItems.map((sub, subIndex) => (
                        <div key={subIndex} style={{ position: 'relative' }} data-tut={sub.label}>
                          <Link
                            href={sub.href}
                            className={`sidebar-sublink${isActive(sub.href) ? " active" : ""}`}
                          >
                            {sub.icon && <span className="mr-2">{sub.icon}</span>}
                            {sub.label}
                          </Link>
                          {isMessagesItem(sub) && (
                            <NotificationBadge count={unreadCount} loading={countLoading} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="sidebar-footer">
              <div className="sidebar-user">
                <div>
                  {user?.name ? (
                    <div className="sidebar-avatar-initials">
                      {fixTextEncoding(user?.name).split(" ").map((part) => part[0]).join("").toUpperCase()}
                    </div>
                  ) : (
                    <User size={32} />
                  )}
                </div>
                <div className="sidebar-user-info">
                  <div className="sidebar-user-name">{fixTextEncoding(user?.name)}</div>
                  <div className="sidebar-user-email">{user?.email}</div>
                </div>
              </div>
              <div className="sidebar-logout flex items-center">
                <LogoutButton />
                <div className="flex-shrink-0 ml-3">
                  <SeericButton
                    variant="tertiary"
                    onClick={() => { router.push("/settings") }}
                  >

                    <Settings size={32} />
                  </SeericButton>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
